/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="common.module.ts" />

module Meevo.Components.Common {

    /**
     * Lot-modal bridge context discriminator. Added to the custom element as the
     * `context` DOM property; also expected on the line-item arm of the
     * LotTrackingLineItem discriminated union inside the web component.
     */
    export type LotModalContext = 'po-receive' | 'inventory-count' | 'inventory-transfer';

    /**
     * Modal mode: which rows are editable vs. locked.
     *   editable — all rows editable (greenfield receive / open count)
     *   partial  — pending rows editable, server-committed rows locked (PO partial-receive flow)
     *   readonly — every row locked (PO closed / count committed)
     */
    export type LotModalMode = 'editable' | 'partial' | 'readonly';

    /**
     * Shared bridge logic for mounting the <meevo-lot-tracking-modal> custom
     * element from AngularJS plugins. Extracted from the original PO-only
     * `lotModal.bridge.ts` so the same host-bridge construction, session
     * teardown wiring, custom-element-registration check, and error path are
     * shared by every plugin that opens the modal (PO, Inventory Count,
     * Inventory Return, ...).
     *
     * Plugin-specific bridge classes (e.g. `Meevo.Plugins.PurchaseOrder.LotModalBridge`,
     * `Meevo.Plugins.InventoryCount.LotModalBridge`) are thin wrappers that
     * inject this helper and call `openModal()` with their own `context`,
     * line-item type, and allocation type.
     *
     * Why a helper service rather than a static utility:
     *   - The host-bridge construction needs $rootScope, $translate, IdentityService,
     *     AppSettings, and ModalService — all AngularJS DI-managed singletons.
     *     Wrapping them in a service keeps the call sites injection-free.
     */
    export class LotModalBridgeHelper {

        private static readonly LOT_MODAL_BUNDLE_URL = 'lib/meevo-lot-tracking-modal.js';

        /** Custom-element tag for the single-line-item modal (grid pencil, PO, transfer). */
        private static readonly SINGLE_MODAL_TAG = 'meevo-lot-tracking-modal';

        /** Custom-element tag for the batch modal (scan-driven multi-line flow). Ships in the
         *  same bundle as the single modal, so preloadElement is reusable as-is. */
        private static readonly BATCH_MODAL_TAG = 'meevo-lot-tracking-batch-modal';

        static $inject = [
            '$rootScope',
            '$translate',
            'meevo.components.common.IdentityService',
            'meevo.AppSettings',
            'meevo.components.modal.ModalService',
        ];

        constructor(
            private $rootScope: angular.IRootScopeService,
            private $translate: angular.translate.ITranslateService,
            private identityService: Meevo.Components.Common.IIdentityService,
            private appSettings: Meevo.Components.Common.IAppSettings,
            private modalService: Meevo.Components.Modal.IModalService,
        ) {
            // Preload the WC bundle as soon as any plugin's wrapper bridge is injected.
            // AngularJS instantiates this service lazily on first injection (singleton),
            // so the script tag lands in <head> when the user enters a page that uses the
            // lot modal — the bundle downloads and registers the custom element in
            // parallel with the rest of page render. By the time the user clicks
            // "Manage Lots" the element is already defined and openModal() opens
            // immediately. Centralizing the preload here means new plugins (Inventory
            // Return, etc.) get the behavior automatically just by injecting this helper.
            LotModalBridgeHelper.preloadElement();
        }

        /**
         * Inject the WC bundle <script> tag into <head>. Idempotent — safe to call multiple
         * times. Guarded by both a script-tag selector check and a customElements.get()
         * check so concurrent calls or repeat invocations from different code paths
         * (constructor preload + defensive call in ensureElementRegistered) never produce
         * duplicate <script> tags.
         */
        private static preloadElement(): void {
            if (document.querySelector('script[src*="meevo-lot-tracking-modal"]')) return;
            if ((window as any).customElements && (window as any).customElements.get('meevo-lot-tracking-modal')) return;
            const s = document.createElement('script');
            s.src = LotModalBridgeHelper.LOT_MODAL_BUNDLE_URL;
            s.async = true;
            document.head.appendChild(s);
        }

        /**
         * Public API: mount the lot-tracking-modal for the supplied line item.
         *
         * Parameterized over the line-item type (TLineItem) and the allocation
         * type (TAllocation) so each plugin's bridge wrapper preserves its own
         * shape end-to-end.
         */
        public openModal<TLineItem, TAllocation>(opts: {
            context: LotModalContext;
            mode: LotModalMode;
            lineItem: TLineItem;
            /** Plugin-specific DOM properties to assign on the custom element.
             *  The helper iterates this map verbatim — it does NOT inspect keys
             *  or apply routing — so plugin-specific inputs stay encapsulated
             *  in each wrapper bridge instead of leaking into shared code.
             *  Known contracts today:
             *    - PO Receive + Inventory Transfer: `{ existingAllocations: VariantLotAllocation[] }`
             *    - Inventory Count: `{ existingCountAllocations: InventoryCountAllocation[], hideOnHand?: boolean }`
             *  Adding a new plugin is a wrapper-only change; the helper does not
             *  need to learn about it.
             */
            domProps?: Record<string, any>;
            onSaved?: (allocations: TAllocation[]) => void;
            onCancelled?: () => void;
        }): void {
            this.ensureElementRegistered(LotModalBridgeHelper.SINGLE_MODAL_TAG)
                .then(() => this.mount(opts))
                .catch(() => this.showLoadFailedAlert());
        }

        /**
         * Public API: mount the batch lot-tracking modal for a set of scanned line items.
         *
         * Mounts `<meevo-lot-tracking-batch-modal>` (vs. the single-item `openModal`). The batch
         * element takes the full set of entries up front (`el.entries`) and emits one `savedBatch`
         * event whose detail is the per-variant results array — instead of the single modal's
         * per-variant `saved`. Shares the same host-bridge construction, session teardown wiring,
         * registration check, and bundle as the single modal (both ship in the same WC bundle).
         *
         * Parameterized over the line-item type (TLineItem) and allocation type (TAllocation) so
         * each plugin's wrapper bridge preserves its own shape end-to-end, mirroring openModal.
         */
        public openBatchModal<TLineItem, TAllocation, TResult>(opts: {
            entries: { lineItem: TLineItem; existingAllocations: TAllocation[] }[];
            /** Batch DOM properties applied on the element. `hideOnHand` / `hadNonLot` mirror the
             *  single modal's redaction + non-lot-presence gates across the whole batch. */
            domProps: { hideOnHand: boolean; hadNonLot: boolean };
            onSavedAll?: (results: TResult[]) => void;
            onCancelled?: () => void;
        }): void {
            this.ensureElementRegistered(LotModalBridgeHelper.BATCH_MODAL_TAG)
                .then(() => this.mountBatch<TLineItem, TAllocation, TResult>(opts))
                .catch(() => this.showLoadFailedAlert());
        }

        /**
         * Resolve when the supplied custom-element tag is registered, or reject after 5s if the
         * bundle failed to load / define it. Callers surface the rejection as a modal alert so the
         * user sees something actionable instead of a silent no-op.
         *
         * 5s rather than 1s — on a cold cache, parsing + executing the WC bundle plus the time
         * for `customElements.define()` to fire can exceed a second on slower machines, so 1s
         * was producing flaky false-rejects. 5s is still snappy enough that a genuinely failed
         * load surfaces an alert quickly, while comfortably accommodating cold-start.
         */
        private ensureElementRegistered(tag: string): Promise<void> {
            if ((window as any).customElements && (window as any).customElements.get(tag)) {
                return Promise.resolve();
            }
            // Defensive: the constructor already preloaded on injection, but call again
            // in case some future code path acquires a helper instance another way.
            // preloadElement is idempotent.
            LotModalBridgeHelper.preloadElement();
            const timeout = new Promise<void>((_resolve, reject) => {
                window.setTimeout(() => reject(new Error(tag + ' registration timed out')), 5000);
            });
            const ready = (window as any).customElements.whenDefined(tag).then(() => undefined);
            return Promise.race([ready, timeout]);
        }

        private mount<TLineItem, TAllocation>(opts: {
            context: LotModalContext;
            mode: LotModalMode;
            lineItem: TLineItem;
            domProps?: Record<string, any>;
            onSaved?: (allocations: TAllocation[]) => void;
            onCancelled?: () => void;
        }): void {
            const el = document.createElement(LotModalBridgeHelper.SINGLE_MODAL_TAG) as any;

            // Bridge-owned props — every plugin shares these. All set as DOM
            // properties (not HTML attributes) so complex objects pass by reference.
            el.hostBridge = this.buildHostBridge();
            el.context = opts.context;
            el.mode = opts.mode;
            el.lineItem = opts.lineItem;

            // Plugin-owned props (e.g. existingAllocations vs. existingCountAllocations,
            // hideOnHand). The helper iterates verbatim — no routing — so plugin-
            // specific DOM-property contracts stay in their wrapper bridges and the
            // helper has no inventory-count or PO-specific knowledge.
            if (opts.domProps) {
                for (const key of Object.keys(opts.domProps)) {
                    el[key] = opts.domProps[key];
                }
            }

            // Single-modal save: the WC emits `saved` with detail.allocations for the one line item.
            const onSavedHandler = (e: any) => {
                const allocations: TAllocation[] = (e && e.detail && e.detail.allocations) || [];
                this.$rootScope.$applyAsync(() => {
                    if (opts.onSaved) { opts.onSaved(allocations); }
                });
            };

            this.wireElementLifecycle(el, 'saved', onSavedHandler, opts.onCancelled);

            document.body.appendChild(el);
        }

        private mountBatch<TLineItem, TAllocation, TResult>(opts: {
            entries: { lineItem: TLineItem; existingAllocations: TAllocation[] }[];
            domProps: { hideOnHand: boolean; hadNonLot: boolean };
            onSavedAll?: (results: TResult[]) => void;
            onCancelled?: () => void;
        }): void {
            const el = document.createElement(LotModalBridgeHelper.BATCH_MODAL_TAG) as any;

            // Bridge-owned props. All set as DOM properties (not HTML attributes) so the
            // entries array / nested objects pass by reference.
            el.hostBridge = this.buildHostBridge();
            el.entries = opts.entries;
            el.hideOnHand = opts.domProps.hideOnHand;
            el.hadNonLot = opts.domProps.hadNonLot;

            // Batch save: the WC emits `savedBatch` with detail being the per-variant results array
            // (vs. the single modal's `saved` → detail.allocations for one line item).
            const onSavedHandler = (e: any) => {
                const results: TResult[] = (e && e.detail) || [];
                this.$rootScope.$applyAsync(() => {
                    if (opts.onSavedAll) { opts.onSavedAll(results); }
                });
            };

            this.wireElementLifecycle(el, 'savedBatch', onSavedHandler, opts.onCancelled);

            document.body.appendChild(el);
        }

        /**
         * Shared lifecycle wiring for both the single and batch modal elements: attaches the
         * save handler (whose event name + payload extraction differ per modal and are supplied
         * by the caller), the `cancelled` and `auth-expired` handlers, and the Shell-driven
         * session-teardown listeners; and tears all of it down (plus removes the node) on the
         * first terminal event.
         *
         * @param el           The mounted custom element.
         * @param savedEvent   The save event name to listen for ('saved' vs. 'savedBatch').
         * @param onSavedHandler  Caller-supplied save handler — owns reading e.detail and invoking
         *                        the plugin callback. It does NOT call cleanup(); cleanup is wrapped
         *                        around it here so both modals share identical teardown.
         * @param onCancelled   Plugin cancel callback (optional).
         */
        private wireElementLifecycle(
            el: any,
            savedEvent: string,
            onSavedHandler: (e: any) => void,
            onCancelled?: () => void,
        ): void {
            // Handlers are declared with `let` so they can reference each other and cleanup() before
            // their own initialization. cleanup() closes over the handler references; handlers close
            // over cleanup. None of them fires synchronously during mount — events are user-driven.
            let savedWrapper: (e: any) => void;
            let onCancelledHandler: () => void;
            let onAuthExpiredHandler: () => void;

            // $rootScope.$on returns an unsubscribe function. We keep refs so cleanup()
            // can detach. Without unsubscribing, every modal open/close leaks a listener
            // onto $rootScope for the session of the page.
            let unsubAutoLogout: (() => void) | null = null;
            let unsubSessionTimeout: (() => void) | null = null;
            let unsubSessionExpiring: (() => void) | null = null;

            const cleanup = () => {
                el.removeEventListener(savedEvent, savedWrapper);
                el.removeEventListener('cancelled', onCancelledHandler);
                el.removeEventListener('auth-expired', onAuthExpiredHandler);
                if (unsubAutoLogout) { unsubAutoLogout(); unsubAutoLogout = null; }
                if (unsubSessionTimeout) { unsubSessionTimeout(); unsubSessionTimeout = null; }
                if (unsubSessionExpiring) { unsubSessionExpiring(); unsubSessionExpiring = null; }
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            };

            savedWrapper = (e: any) => {
                cleanup();
                onSavedHandler(e);
            };
            onCancelledHandler = () => {
                cleanup();
                this.$rootScope.$applyAsync(() => {
                    if (onCancelled) { onCancelled(); }
                });
            };
            onAuthExpiredHandler = () => {
                cleanup();
                this.$rootScope.$applyAsync(() => {
                    // Route the 401 through the Shell's standard session-expired path:
                    //   1. Emit SERVER_VALIDATION so main.controller's $on handler shows the session-expired modal.
                    //   2. Clear the bearer token so subsequent requests don't keep hitting 401.
                    // (Matches the pattern in exception.interceptor.ts's unauthorizedAccessHandler.)
                    this.$rootScope.$emit(
                        Meevo.Components.Common.Messages.SERVER_VALIDATION,
                        this.$translate.instant('ERROR_SESSION_EXPIRED'),
                        Meevo.Model.NotificationModeType.Modal,
                        true,
                    );
                    this.identityService.clearToken();
                });
            };

            el.addEventListener(savedEvent, savedWrapper);
            el.addEventListener('cancelled', onCancelledHandler);
            el.addEventListener('auth-expired', onAuthExpiredHandler);

            // Shell-driven session teardown paths. The WC's own auth-expired event only
            // fires when an HTTP 401 is observed by its interceptor; an INACTIVITY-driven
            // logout tears down the session without any request, so the WC would never
            // know. Without these listeners the lot modal stayed on top of the Shell's
            // lockout / login dialog. See the original PO bridge for the long-form rationale.
            unsubSessionExpiring = this.$rootScope.$on(
                Meevo.Components.Common.Messages.SESSION_EXPIRING,
                () => cleanup(),
            );
            unsubAutoLogout = this.$rootScope.$on(
                Meevo.Components.Common.Messages.AUTO_LOGOUT_USER,
                () => cleanup(),
            );
            unsubSessionTimeout = this.$rootScope.$on(
                Meevo.Components.Common.Messages.SESSION_TIMEOUT_HANDLER,
                () => cleanup(),
            );
        }

        /**
         * Build the MeevoHostBridge-shaped object the web component expects.
         *
         * Subscribe is omitted: the bridge never needs to push context changes to the WC while the
         * modal is open (the modal's lifecycle is a single mount). If that ever changes, wire up a
         * listener backed by $rootScope.$on(...) and return an unsubscribe function.
         */
        private buildHostBridge(): any {
            return {
                initialize: () => this.buildBootstrapContext(),
                getCurrentContext: () => this.buildBootstrapContext(),
                refreshLockoutLastEventTime: () => { /* no-op — host-shell drives lockout independently */ },
            };
        }

        /**
         * Build a MeevoHostBootstrapContext from the AngularJS identity + app settings.
         *
         * NOTE on bearer token: The Shell does NOT expose a getToken() on IdentityService — the token
         * is written to window.bearerToken + sessionStorage by IdentityService.setToken() at login.
         * This mirrors the canonical pattern in interop.service.ts's getSession() which reads
         * window.bearerToken and spreads it onto the identity payload.
         */
        private buildBootstrapContext(): any {
            const identity = this.identityService.current();
            const win = window as any;
            const bearerToken = win.bearerToken || sessionStorage.getItem('mil:meevo:token') || '';
            const localeId = this.$translate.use() || (this.appSettings && this.appSettings.APP_LANGUAGEID) || '';

            return {
                apiBaseUrl: (this.appSettings && this.appSettings.API_ROOT) || '',
                session: {
                    bearerToken: bearerToken,
                    sessionId: (identity && (identity as any).sessionId) || '',
                    tenantId: identity ? identity.tenantId : null,
                    userId: identity ? identity.userId : null,
                    userName: identity ? identity.userName : null,
                    displayName: identity ? identity.displayName : null,
                    locationId: identity ? identity.locationId : null,
                    locationName: identity ? identity.locationName : null,
                    localeId: localeId,
                    isAuthenticated: !!(identity && identity.isAuthenticated),
                },
                appSettings: {
                    API_ROOT: (this.appSettings && this.appSettings.API_ROOT) || '',
                    APP_LANGUAGEID: localeId,
                    APP_COUNTRYID: (this.appSettings && this.appSettings.APP_COUNTRYID) || '',
                    APP_ISOCOUNTRYCODE: (this.appSettings && this.appSettings.APP_ISOCOUNTRYCODE) || '',
                },
                securityItems: [],
            };
        }

        /**
         * Shown when the WC bundle failed to register the custom element (network error, bundle
         * not loaded, etc.). Uses modalService (not $mdDialog directly) to match the Shell's
         * established pattern — keys fall back to literal text if translations aren't yet added.
         */
        private showLoadFailedAlert(): void {
            this.modalService.alert(
                'ERROR_LOT_MODAL_LOAD_FAILED',
                Meevo.Components.Modal.ModalTranslationModes.Translate,
                true,
            );
        }
    }

    angular.module('meevo.components.common')
        .service('meevo.components.common.LotModalBridgeHelper', LotModalBridgeHelper);
}
